import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, Search, LockKeyhole, LogOut, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../i18n';
import SEO from '../components/SEO';
import { FadeIn } from '../components/motion';
import ProductCard from '../components/ProductCard';
import ProductModal from '../components/ProductModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loginWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + '/catalogo';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const AuthWall = () => {
  const { t } = useLang();
  return (
    <FadeIn delay={0.1} className="max-w-2xl mx-auto">
      <div className="bg-card border border-foreground/10 p-8 md:p-14 text-center" data-testid="auth-wall">
        <span className="inline-flex items-center justify-center w-14 h-14 bg-primary text-primary-foreground mb-8">
          <LockKeyhole className="w-6 h-6" strokeWidth={1.5} />
        </span>
        <span className="block font-display text-xs uppercase tracking-[0.25em] font-bold text-secondary mb-4" data-testid="auth-wall-eyebrow">
          {t('authwall.eyebrow')}
        </span>
        <h2 className="font-serif font-light text-3xl md:text-5xl tracking-tight leading-[1.05] mb-5 text-balance" data-testid="auth-wall-title">
          {t('authwall.title')}
        </h2>
        <p className="text-foreground/60 leading-relaxed max-w-md mx-auto mb-10" data-testid="auth-wall-sub">{t('authwall.sub')}</p>
        <button
          onClick={loginWithGoogle}
          data-testid="google-login-button"
          className="inline-flex items-center gap-3 bg-secondary text-white px-10 py-4 font-display font-bold text-sm uppercase tracking-wider hover:bg-primary transition-colors duration-300"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z"/></svg>
          {t('authwall.login')}
        </button>
        <p className="flex items-center justify-center gap-2 text-xs text-foreground/40 mt-6">
          <ShieldCheck className="w-3.5 h-3.5" />
          {t('authwall.note')}
        </p>
      </div>
    </FadeIn>
  );
};

const Catalogo = () => {
  const { lang, t } = useLang();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get('cat') || 'all';
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (location.state?.user) return;
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => { setUser(r.data); setAuth(true); })
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth !== true) return;
    axios.get(`${API}/products`, { withCredentials: true })
      .then((r) => setProducts(r.data.products))
      .catch((err) => {
        if (err?.response?.status === 401) setAuth(false);
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, [auth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setAuth(false);
      setUser(null);
      setProducts([]);
      toast.success(t('authwall.logout'));
    }
  };

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const okCat = cat === 'all' || p.category === cat;
      const okQ = !query || p.name.toLowerCase().includes(query.toLowerCase());
      return okCat && okQ;
    });
  }, [products, cat, query]);

  const filters = [
    { key: 'all', label: t('catalogo.all') },
    { key: 'kris', label: t('catalogo.kris') },
    { key: 'fritti', label: t('catalogo.fritti') },
    { key: 'primi', label: t('catalogo.primi') },
    { key: 'secondi', label: t('catalogo.secondi') },
    { key: 'contorni', label: t('catalogo.contorni') },
    { key: 'sughi', label: t('catalogo.sughi') },
    { key: 'dolci', label: t('catalogo.dolci') },
  ];

  const counts = useMemo(() => {
    const c = { all: products.length };
    products.forEach((p) => { c[p.category] = (c[p.category] || 0) + 1; });
    return c;
  }, [products]);

  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-32" data-testid="catalogo-page">
      <SEO page="catalogo" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <FadeIn y={16} className="mb-6">
          <span className="inline-flex items-center gap-3 font-display text-xs uppercase tracking-[0.25em] font-bold text-secondary">
            <span className="w-10 h-px bg-secondary inline-block" />
            {t('catalogo.eyebrow')}
          </span>
        </FadeIn>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <FadeIn delay={0.1}>
            <h1 className="font-display font-extrabold text-5xl sm:text-6xl md:text-7xl tracking-tighter leading-[0.9]" data-testid="catalogo-title">
              {t('catalogo.title')}
            </h1>
            <p className="text-foreground/60 text-base md:text-lg max-w-xl mt-6" data-testid="catalogo-sub">{t('catalogo.sub')}</p>
          </FadeIn>
          {auth === true && (
            <FadeIn delay={0.2} className="flex flex-col items-start lg:items-end gap-4">
              <a
                href={`${API}/catalog.pdf`}
                data-testid="download-catalog-button"
                className="group inline-flex items-center gap-3 border-2 border-foreground px-6 py-3.5 font-display font-bold text-sm uppercase tracking-wider hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-300"
              >
                <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                {t('catalogo.download')}
              </a>
              <div className="flex items-center gap-3 text-sm text-foreground/60" data-testid="user-bar">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
                ) : null}
                <span>{t('authwall.welcome')}{user?.name ? `, ${user.name}` : ''}</span>
                <button
                  onClick={logout}
                  data-testid="logout-button"
                  className="inline-flex items-center gap-1.5 font-display font-bold text-xs uppercase tracking-widest text-secondary hover:text-primary transition-colors duration-300"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  {t('authwall.logout')}
                </button>
              </div>
            </FadeIn>
          )}
        </div>

        {auth === null ? (
          <div className="py-24 flex flex-col items-center gap-4" data-testid="catalog-checking">
            <Loader2 className="w-7 h-7 animate-spin text-secondary" />
            <p className="text-sm text-foreground/50 font-display uppercase tracking-widest">{t('authwall.checking')}</p>
          </div>
        ) : auth === false ? (
          <AuthWall />
        ) : (
          <>
            <div className="sticky top-16 md:top-20 z-30 bg-background/85 backdrop-blur-xl border-y border-foreground/10 -mx-5 md:-mx-10 px-5 md:px-10 py-4 mb-12" data-testid="filter-bar">
              <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0">
                  {filters.map((f) => (
                    <button
                      key={f.key}
                      data-testid={`filter-${f.key}-button`}
                      onClick={() => setSearchParams(f.key === 'all' ? {} : { cat: f.key })}
                      className={`shrink-0 px-5 py-2.5 font-display font-bold text-xs uppercase tracking-widest border transition-colors duration-300 ${
                        cat === f.key
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-foreground/15 text-foreground/60 hover:border-foreground hover:text-foreground'
                      }`}
                    >
                      {f.label}
                      <span className="ml-2 opacity-50">{counts[f.key] ?? 0}</span>
                    </button>
                  ))}
                </div>
                <div className="relative md:w-64 shrink-0">
                  <Search className="w-4 h-4 absolute left-0 top-1/2 -translate-y-1/2 text-foreground/40" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('catalogo.search')}
                    data-testid="catalog-search-input"
                    className="w-full bg-transparent border-b-2 border-foreground/20 pl-7 py-2 text-sm focus:border-secondary focus:outline-none transition-colors duration-300 placeholder-foreground/40"
                  />
                </div>
              </div>
            </div>

            <p className="text-sm text-foreground/50 mb-8" data-testid="catalog-results-count">
              {filtered.length} {t('catalogo.results')}
            </p>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="catalog-loading">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="border border-foreground/10 animate-pulse">
                    <div className="aspect-square bg-muted" />
                    <div className="p-5"><div className="h-5 bg-muted w-3/4" /><div className="h-4 bg-muted w-1/4 mt-3" /></div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-24 text-center text-foreground/50 font-serif italic text-2xl" data-testid="catalog-no-results">{t('catalogo.noResults')}</p>
            ) : (
              <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="catalog-grid">
                <AnimatePresence mode="popLayout">
                  {filtered.map((p, i) => (
                    <ProductCard key={p.slug} product={p} index={i} onOpen={setSelected} />
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </>
        )}
      </div>
      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </main>
  );
};

export default Catalogo;
